/**
 * Popup functionality
 */
import { lockScroll } from './utils.js';

export function initPopup() {
    // Scroll detection variables
    let scrollAccumulator = 0;
    let lastWheelTime = 0;
    let touchStartY = 0;
    
    /**
     * Open popup
     * @param {string} popupId 
     */
    function openPopup(popupId) {
        const popup = document.getElementById(popupId);
        if (popup) {
            const overlay = popup.querySelector('.popup__overlay');
            const content = popup.querySelector('.popup__content');
            
            // Clear any inline styles from wheel interaction
            popup.style.opacity = '';
            popup.style.transform = '';
            popup.style.display = '';
            popup.style.visibility = '';
            
            if (overlay) {
                overlay.style.transform = '';
                overlay.style.opacity = '';
            }
            
            if (content) {
                content.style.transform = '';
                content.style.opacity = '';
            }
            
            popup.classList.add('popup--active');
            lockScroll(true);
            
            // Add scroll listener for contact form overlay animation
            if (popupId === 'contact-popup') {
                setupContactFormOverlayAnimation(popup);
            }
        }
    }
    
    /**
     * Setup overlay animation for contact form scrolling
     * @param {Element} popup 
     */
    function setupContactFormOverlayAnimation(popup) {
        const formWrapper = popup.querySelector('.contact-form-wrapper');
        const overlay = popup.querySelector('.popup__overlay');
        
        if (!formWrapper || !overlay) return;
        
        // Check if mobile (max-width: 768px)
        const isMobile = window.innerWidth <= 768;
        
        if (isMobile) {
            // On mobile, no animation
            overlay.style.top = '';
            overlay.style.bottom = '';
            return;
        }
        
        let lastScrollTop = 0;
        
        let scrollHandler = () => {
            const scrollTop = formWrapper.scrollTop;
            
            if (scrollTop > lastScrollTop) {
                // Scrolling down
                overlay.style.top = 'auto';
                overlay.style.bottom = '80px';
            } else {
                // Scrolling up
                overlay.style.top = '40px';
                overlay.style.bottom = 'auto';
            }
            
            lastScrollTop = scrollTop;
        };
        
        // Set initial state for desktop only
        overlay.style.top = '40px';
        overlay.style.bottom = 'auto';
        
        formWrapper.addEventListener('scroll', scrollHandler);
        
        // Store handler for cleanup
        formWrapper._overlayScrollHandler = scrollHandler;
    }
    
    /**
     * Close popup with smooth fade down animation
     * @param {string} popupId 
     */
    function closePopup(popupId) {
        const popup = document.getElementById(popupId);
        if (popup && popup.classList.contains('popup--active')) {
            const overlay = popup.querySelector('.popup__overlay');
            const content = popup.querySelector('.popup__content');
            
            // Clean up scroll listener for contact form overlay animation
            if (popupId === 'contact-popup') {
                const formWrapper = popup.querySelector('.contact-form-wrapper');
                if (formWrapper && formWrapper._overlayScrollHandler) {
                    formWrapper.removeEventListener('scroll', formWrapper._overlayScrollHandler);
                    delete formWrapper._overlayScrollHandler;
                }
            }
            
            // Clear any inline styles from wheel interaction
            popup.style.opacity = '';
            popup.style.transform = '';
            popup.style.visibility = '';
            
            if (overlay) {
                overlay.style.transform = '';
                overlay.style.opacity = '';
                overlay.style.top = '';
                overlay.style.bottom = '';
                overlay.style.transition = '';
            }
            
            if (content) {
                content.style.transform = '';
                content.style.opacity = '';
            }
            
            // Add closing class for animation
            popup.classList.add('popup--closing');
            popup.classList.remove('popup--active');
            
            // Wait for animation to complete, then fully hide
            setTimeout(() => {
                popup.classList.remove('popup--closing');
                popup.style.display = '';
                lockScroll(false);
            }, 600); // Match the CSS transition duration
        }
    }
    
    // Close popup when clicking close button or overlay
    document.querySelectorAll('.popup__close, .popup__overlay').forEach(element => {
        element.addEventListener('click', function() {
            const popupId = this.getAttribute('data-popup');
            if (popupId) {
                closePopup(popupId);
            }
        });
    });
    
    // Close popup with Escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            const activePopup = document.querySelector('.popup--active');
            if (activePopup) {
                const popupId = activePopup.id;
                closePopup(popupId);
            }
        }
    });
    
    // Contact button click handler by ID
    const contactBtn = document.getElementById('contactBtn');
    if (contactBtn) {
        contactBtn.addEventListener('click', function(e) {
            e.preventDefault();
            openPopup('contact-popup');
        });
    }
    
    // Also keep href selector as fallback
    const contactLinks = document.querySelectorAll('a[href="#contact"]');
    contactLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            e.preventDefault();
            openPopup('contact-popup');
        });
    });
    
    // Removed automatic contact popup for mailto links - let them work normally
    // const emailLinks = document.querySelectorAll('a[href^="mailto:"]');
    // emailLinks.forEach(link => {
    //     link.addEventListener('click', function(e) {
    //         e.preventDefault();
    //         openPopup('contact-popup');
    //     });
    // });
    
    // Wheel event detection for popup control with visual feedback
    window.addEventListener('wheel', function(e) {
        const now = Date.now();
        const timeDiff = now - lastWheelTime;
        
        // Reset accumulator if too much time has passed
        if (timeDiff > 500) {
            scrollAccumulator = 0;
        }
        
        scrollAccumulator += e.deltaY;
        lastWheelTime = now;
        
        const activePopup = document.querySelector('.popup--active');
        const closingPopup = document.querySelector('.popup--closing');
        
        // Threshold for opening/closing popup
        const threshold = 300;
        
        if (scrollAccumulator > threshold && !activePopup && !closingPopup) {
            // Scrolled down - open popup
            openPopup('contact-popup');
            scrollAccumulator = 0;
        } else if (scrollAccumulator < -threshold && activePopup) {
            // Scrolled up - close popup
            closePopup('contact-popup');
            scrollAccumulator = 0;
        }
    }, { passive: true });
    
    // Touch events for mobile
    window.addEventListener('touchstart', function(e) {
        touchStartY = e.touches[0].clientY;
    }, { passive: true });
    
    window.addEventListener('touchmove', function(e) {
        const touchCurrentY = e.touches[0].clientY;
        const touchDiff = touchStartY - touchCurrentY;
        
        const activePopup = document.querySelector('.popup--active');
        const closingPopup = document.querySelector('.popup--closing');
        
        // Threshold for touch gestures
        const touchThreshold = 100;
        
        if (touchDiff > touchThreshold && !activePopup && !closingPopup) {
            // Swiped up (scrolled down) - open popup
            openPopup('contact-popup');
            touchStartY = touchCurrentY; // Reset
        } else if (touchDiff < -touchThreshold && activePopup) {
            // Swiped down (scrolled up) - close popup
            closePopup('contact-popup');
            touchStartY = touchCurrentY; // Reset
        }
    }, { passive: true });
    
    // Make functions global for external use
    window.openPopup = openPopup;
    window.closePopup = closePopup;
}